import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withCtx as _withCtx, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["id", "for"]
const _hoisted_2 = { class: "sr-only" }
const _hoisted_3 = {
  key: 0,
  class: "dk-search__label-icon"
}
const _hoisted_4 = { class: "dk-search-group" }
const _hoisted_5 = { class: "dk-search-group__animation-mask" }
const _hoisted_6 = { class: "dk-search-group__prepend" }
const _hoisted_7 = { class: "dk-search__chip-icon" }
const _hoisted_8 = { class: "dk-search__chip-icon" }
const _hoisted_9 = ["id", "name", "aria-label", "placeholder", "autofocus", "autocomplete"]
const _hoisted_10 = {
  key: 0,
  class: "dk-search-group__append"
}

import {
  computed,
  ref,
  toRefs,
  PropType,
  onMounted,
  onBeforeUnmount,
  watch,
  ComponentPublicInstance,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useMq } from 'vue3-mq';
import { useRoute } from 'vue-router';

import { DKButton, DKIcon } from '@dormakaba/dormakaba-components';
import { isMobile as $isMobile } from '@dormakaba/dormakaba-components';

const suggestionsSliderIntervalDuration = 4000;
const suggestionTypeTimeoutDurationEnd = 250;
const suggestionTypeTimeoutDurationMax = 100;
const suggestionTypeTimeoutDurationMin = 50;


export default /*#__PURE__*/_defineComponent({
  __name: 'DKSearch',
  props: {
  id: {
    type: String,
    default: 'search',
  },
  label: {
    type: String,
    default: 'Search',
  },
  searchTerm: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'primary',
  },
  isFluid: {
    type: Boolean,
    default: false,
  },
  isFooter: {
    type: Boolean,
    default: false,
  },
  isInline: {
    type: Boolean,
    default: false,
  },
  isHamburger: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: Boolean,
    default: false,
  },
  searchSuggestions: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
},
  emits: ["search", "clickSearchSuggestion", "remove"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const props = __props;

const { t } = useI18n();
const mq = useMq();

const route = useRoute();

const { searchTerm, searchSuggestions } = toRefs(props);

const isGroupAnimated = ref<boolean>(false);
const isSearchChipHidden = ref<boolean>(false);
const isChipButton = ref<boolean>(false);
const removeChipAfterAnimationOut = ref<boolean>(false);
const isPlaceholderHidden = ref<boolean>(false);
const isChipAnimatedOutFromInput = ref<boolean>(false);
const isSuggestionChipAnimatedOut = ref<boolean>(false);
const isMobileViewport = ref<boolean>(false);

const searchTermValue = ref<string>('');
const previousSearchTerm = ref<string>('');
const suggestionTerm = ref<string>('');
const currentSuggestionTerm = ref<string>('');

const currentSuggestionTermCharIndex = ref<number>(0);

const searchChipButton = ref<ComponentPublicInstance<HTMLButtonElement> | null>(
  null
);
const searchGroupAnimation =
  ref<ComponentPublicInstance<HTMLButtonElement> | null>(null);
const suggestionChipButton =
  ref<ComponentPublicInstance<HTMLButtonElement> | null>(null);
const previousChipButton =
  ref<ComponentPublicInstance<HTMLButtonElement> | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

const suggestionsSliderInterval = ref<ReturnType<typeof setInterval>>();
const suggestionTypeTimeout = ref<ReturnType<typeof setInterval>>();

const isMobile = computed<boolean>(() => $isMobile(mq));
const labelIconSize = computed<number>(() => (isMobile.value ? 24 : 32));
const labelText = computed<string>(
  () => props.label ?? t('search.input.label')
);
const placeholderText = computed<string>(() =>
  !searchTerm.value ? props.placeholder : ''
);

onMounted(() => {
  initSearchChip();
  setSearchVariant();

  setSuggestions();
});

onBeforeUnmount(() => {
  stopSuggestionsSlider();
});

const onSubmit = (e: Event) => {
  if (isGroupAnimated.value) {
    e.preventDefault();
    return;
  }

  if (searchTermValue.value) {
    emit('search', searchTermValue.value);
    searchTermValue.value = '';
  } else if (searchTerm.value) {
    emit('search', searchTerm.value);
  }

  setChipAsButton();
};

const setChipAsButton = () => {
  isSearchChipHidden.value = false;
  isChipButton.value = false;

  setTimeout(() => {
    isChipButton.value = true;
  }, 10);

  if (searchChipButton.value || suggestionChipButton.value) {
    if (searchChipButton.value) {
      previousSearchTerm.value = searchTerm.value;
    } else if (suggestionChipButton.value) {
      previousSearchTerm.value = suggestionTerm.value;
    }

    setTimeout(() => {
      animateChipOut(previousChipButton.value?.$el);
    }, 10);
  }
};

const onClickSearchSuggestion = () => {
  emit('clickSearchSuggestion', suggestionTerm.value);
};

const onRemove = () => {
  if (!props.isHamburger) {
    removeChipAfterAnimationOut.value = true;
    isPlaceholderHidden.value = true;
  }

  animateChipOut(searchChipButton.value?.$el);
};

const animateChipOut = ($chipButton: HTMLElement) => {
  isGroupAnimated.value = true;

  window.requestAnimationFrame(() => {
    searchGroupAnimation.value?.style.setProperty(
      '--t-x',
      getSearchTermWidth($chipButton) + 'px'
    );

    searchGroupAnimation.value?.addEventListener(
      'transitionend',
      animateChipOutEndListener
    );
  });
};

const getSearchTermWidth = ($chipButton: HTMLElement): number => {
  const marginRight = parseInt(
    window.getComputedStyle($chipButton).marginRight.split('px')[0]
  );

  const { width } = $chipButton.getBoundingClientRect();

  return (marginRight + width) * -1;
};

const animateChipOutEndListener = (e: Event) => {
  if (e.target !== searchGroupAnimation.value) {
    return;
  }

  searchGroupAnimation.value?.removeEventListener(
    'transitionend',
    animateChipOutEndListener
  );

  previousSearchTerm.value = '';
  isGroupAnimated.value = false;
  isPlaceholderHidden.value = false;

  window.requestAnimationFrame(() => {
    searchGroupAnimation.value?.style.setProperty('--t-x', '0px');
  });

  if (props.isHamburger) {
    isSearchChipHidden.value = true;
    inputRef.value?.focus();
  }

  if (isChipAnimatedOutFromInput.value) {
    isChipAnimatedOutFromInput.value = false;
    isSearchChipHidden.value = true;
  }

  if (isSuggestionChipAnimatedOut.value) {
    isSuggestionChipAnimatedOut.value = false;
    suggestionTerm.value = '';
  }

  if (removeChipAfterAnimationOut.value) {
    removeChipAfterAnimationOut.value = false;
    processRemove();
  }
};

const setSuggestions = () => {
  if (searchSuggestions.value.length && !searchTermValue.value) {
    animateSuggestion();
    launchSuggestionsSlider();
  }
};

const processRemove = () => {
  emit('remove');
  inputRef.value?.focus();
};

const animateSuggestion = () => {
  setCurrentSuggestion();
  typeSuggestion();
};

const launchSuggestionsSlider = () => {
  reinitInterval(suggestionsSliderInterval.value);

  suggestionsSliderInterval.value = setInterval(
    animateSuggestion,
    suggestionsSliderIntervalDuration
  );
};

const setCurrentSuggestion = () => {
  if (currentSuggestionTerm.value === '') {
    currentSuggestionTerm.value = searchSuggestions.value[0];
  } else {
    const currentIndex = searchSuggestions.value.indexOf(
      currentSuggestionTerm.value
    );

    const nextIndex =
      currentIndex >= searchSuggestions.value.length - 1 ? 0 : currentIndex + 1;

    currentSuggestionTerm.value = searchSuggestions.value[nextIndex];
  }
};

const typeSuggestion = () => {
  searchTermValue.value = currentSuggestionTerm.value.substring(
    0,
    currentSuggestionTermCharIndex.value
  );

  reinitTimeout(suggestionTypeTimeout.value);

  if (
    currentSuggestionTermCharIndex.value <= currentSuggestionTerm.value.length
  ) {
    currentSuggestionTermCharIndex.value++;
    suggestionTypeTimeout.value = setTimeout(
      typeSuggestion,
      getTypingDuration()
    );
  } else {
    currentSuggestionTermCharIndex.value = 0;
    suggestionTypeTimeout.value = setTimeout(
      enterSuggestion,
      suggestionTypeTimeoutDurationEnd
    );
  }

  if (
    isMobileViewport.value &&
    suggestionChipButton.value &&
    !isSuggestionChipAnimatedOut.value
  ) {
    animateChipOut(suggestionChipButton.value?.$el);
    isSuggestionChipAnimatedOut.value = true;
  }
};

const enterSuggestion = () => {
  setChipAsButton();
  suggestionTerm.value = searchTermValue.value;
  isPlaceholderHidden.value = true;

  searchTermValue.value = '';

  if (!suggestionChipButton.value) {
    setTimeout(() => {
      isPlaceholderHidden.value = false;
    }, 10);
  }
};

const getTypingDuration = (): number =>
  Math.floor(
    Math.random() *
      (suggestionTypeTimeoutDurationMax - suggestionTypeTimeoutDurationMin)
  ) + suggestionTypeTimeoutDurationMin;

const setSearchVariant = () => {
  isMobileViewport.value = mq.current === 'sm';
};

const initSearchChip = () => {
  isChipButton.value = route.query.q !== undefined;
};

const stopSuggestionsSlider = () => {
  reinitInterval(suggestionsSliderInterval.value);
  reinitTimeout(suggestionTypeTimeout.value);

  suggestionsSliderInterval.value = undefined;
  suggestionTypeTimeout.value = undefined;

  searchTermValue.value = '';

  if (suggestionChipButton.value) {
    animateChipOut(suggestionChipButton.value?.$el);
    isSuggestionChipAnimatedOut.value = true;
  }
};

const onFocusIn = () => {
  if (suggestionsSliderInterval.value || suggestionTypeTimeout.value) {
    stopSuggestionsSlider();
  }
};

const onFocusOut = () => {
  setSuggestions();
};

const onInput = () => {
  if (isMobileViewport.value && searchChipButton.value) {
    animateChipOut(searchChipButton.value?.$el);
    isChipAnimatedOutFromInput.value = true;
  }
};

const reinitInterval = (ref?: ReturnType<typeof setInterval>) => {
  if (ref) {
    clearInterval(ref);
  }
};

const reinitTimeout = (ref?: ReturnType<typeof setTimeout>) => {
  if (ref) {
    clearTimeout(ref);
  }
};

watch(() => mq.current, setSearchVariant);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
      'dk-search',
      {
        'dk-search--primary': __props.type === 'primary',
        'dk-search--secondary': __props.type === 'secondary',
        'dk-search--fluid': __props.isFluid,
      },
    ])
  }, [
    _createElementVNode("form", {
      class: "dk-search__form",
      onSubmit: _withModifiers(onSubmit, ["prevent"])
    }, [
      _createElementVNode("div", {
        class: _normalizeClass([
          'dk-search__inner',
          {
            'dk-search__inner--footer': __props.isFooter,
          },
        ])
      }, [
        _createElementVNode("label", {
          id: `${__props.id}-label`,
          for: __props.id,
          class: _normalizeClass(["dk-search__label", { 'mr-0': __props.isInline }])
        }, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(labelText.value), 1),
          (!__props.isInline)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(_unref(DKIcon), {
                  icon: "search-bold",
                  size: labelIconSize.value
                }, null, 8, ["size"])
              ]))
            : _createCommentVNode("", true)
        ], 10, _hoisted_1),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", {
              class: _normalizeClass(["dk-search-group__animation", { 'is-animated': isGroupAnimated.value }]),
              ref_key: "searchGroupAnimation",
              ref: searchGroupAnimation
            }, [
              _createElementVNode("div", _hoisted_6, [
                (previousSearchTerm.value)
                  ? (_openBlock(), _createBlock(_unref(DKButton), {
                      key: 0,
                      label: previousSearchTerm.value,
                      class: "dk-search__chip is-button",
                      ref_key: "previousChipButton",
                      ref: previousChipButton
                    }, {
                      "icon-right": _withCtx(() => [
                        _createElementVNode("span", _hoisted_7, [
                          (!suggestionTerm.value)
                            ? (_openBlock(), _createBlock(_unref(DKIcon), {
                                key: 0,
                                icon: "close-bold",
                                size: 20
                              }))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _: 1
                    }, 8, ["label"]))
                  : _createCommentVNode("", true),
                (suggestionTerm.value)
                  ? (_openBlock(), _createBlock(_unref(DKButton), {
                      key: 1,
                      class: _normalizeClass(["dk-search__chip dk-search__chip--suggestion", { 'is-button': isChipButton.value }]),
                      label: suggestionTerm.value,
                      ref_key: "suggestionChipButton",
                      ref: suggestionChipButton,
                      onClick: onClickSearchSuggestion
                    }, null, 8, ["label", "class"]))
                  : _createCommentVNode("", true),
                (_unref(searchTerm) && !isSearchChipHidden.value)
                  ? (_openBlock(), _createBlock(_unref(DKButton), {
                      key: 2,
                      class: _normalizeClass(["dk-search__chip", {
                    'is-button': isChipButton.value,
                    'dk-button--secondary': isMobile.value,
                  }]),
                      label: _unref(searchTerm),
                      ref_key: "searchChipButton",
                      ref: searchChipButton,
                      onClick: onRemove
                    }, {
                      "icon-right": _withCtx(() => [
                        _createElementVNode("span", _hoisted_8, [
                          _createVNode(_unref(DKIcon), {
                            icon: "close-bold",
                            size: 20
                          })
                        ])
                      ]),
                      _: 1
                    }, 8, ["label", "class"]))
                  : _createCommentVNode("", true)
              ]),
              _withDirectives(_createElementVNode("input", {
                id: __props.id,
                ref_key: "inputRef",
                ref: inputRef,
                type: "text",
                class: _normalizeClass(["dk-search__input", { 'is-hidden': isPlaceholderHidden.value }]),
                name: __props.id,
                "aria-label": `${__props.id}-label`,
                placeholder: placeholderText.value,
                autofocus: __props.autofocus,
                autocomplete: __props.autocomplete ? 'on' : 'off',
                spellcheck: false,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTermValue).value = $event)),
                onFocusin: onFocusIn,
                onFocusout: onFocusOut,
                onInput: onInput
              }, null, 42, _hoisted_9), [
                [_vModelText, searchTermValue.value]
              ])
            ], 2)
          ]),
          (__props.isInline)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createVNode(_unref(DKIcon), {
                  icon: "search-bold",
                  size: 20
                })
              ]))
            : _createCommentVNode("", true)
        ])
      ], 2)
    ], 32)
  ], 2))
}
}

})